import {
  ReactNode,
  useCallback,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { subscribeToStreamStateMessages } from "./AppContext";
import { StreamStateValue } from "../components/StreamState";
import { useAuth } from "./AuthContext";
import { useAppContext } from "./AppContext";

type StreamStateDetails = {
  state: StreamStateValue;
  message?: string;
};

type StreamStatesMap = Record<string, StreamStateDetails>;

type StreamsStateContextValue = {
  streamStates: StreamStatesMap;
  getStreamState: (streamId: string | null | undefined) => StreamStateValue;
  getStreamStateMessage: (streamId: string | null | undefined) => string | null;
  setStreamState: (streamId: string | null | undefined, state: StreamStateValue, message?: string | null) => void;
};

const snapshotEventType = "streamStateSnapshot";
const changedEventType = "streamStateChanged";
const fallbackRefreshIntervalMs = 5000;

const StreamsStateContext = createContext<StreamsStateContextValue | undefined>(undefined);

type SnapshotPayload = {
  states?: Record<string, unknown> | null;
};

type UpdatePayload = {
  streamId?: string;
  state?: unknown;
  message?: unknown;
};

const isStreamStateValue = (value: string): value is StreamStateValue => {
  const normalized = value.trim().toLowerCase();
  return (
    normalized === "online" ||
    normalized === "offline" ||
    normalized === "timeout" ||
    normalized === "unknown" ||
    normalized === "error"
  );
};

const parseState = (value: unknown): StreamStateValue | null => {
  if (typeof value !== "string") {
    return null;
  }
  const normalized = value.trim().toLowerCase();
  if (isStreamStateValue(normalized)) {
    return normalized;
  }
  return null;
};

const parseMessage = (value: unknown): string | undefined => {
  if (typeof value !== "string") {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
};

const parseSnapshotEntry = (value: unknown): StreamStateDetails | null => {
  if (typeof value === "string") {
    const state = parseState(value);
    return state ? { state } : null;
  }
  if (!value || typeof value !== "object") {
    return null;
  }

  const candidate = value as { state?: unknown; message?: unknown };
  const state = parseState(candidate.state);
  if (!state) {
    return null;
  }

  const message = parseMessage(candidate.message);
  return message ? { state, message } : { state };
};

export const StreamsStateProvider = ({ children }: { children: ReactNode }) => {
  const [streamStates, setStreamStates] = useState<StreamStatesMap>({});
  const { authorizedFetch } = useAuth();
  const { eventStreamConnected, connectionId } = useAppContext();
  const wasConnectedRef = useRef(eventStreamConnected);
  const refreshInFlightRef = useRef<Promise<void> | null>(null);
  const syncedConnectionIdRef = useRef<string | null>(null);

  const applySnapshot = useCallback((payload: SnapshotPayload) => {
    try {
      const entries = payload?.states ?? {};
      const nextStates: StreamStatesMap = {};
      if (entries && typeof entries === "object") {
        for (const [rawId, value] of Object.entries(entries)) {
          const id = rawId.trim();
          const details = parseSnapshotEntry(value);
          if (!id || !details) {
            continue;
          }
          nextStates[id] = details;
        }
      }
      setStreamStates(nextStates);
    } catch (error) {
      console.error("Failed to parse stream state snapshot", error);
    }
  }, []);

  const refreshStates = useCallback(async () => {
    const response = await authorizedFetch("/api/streams/states");
    const payload = (await response.json().catch(() => null)) as SnapshotPayload | null;
    if (!response.ok) {
      throw new Error("Unable to refresh stream states");
    }
    applySnapshot(payload ?? {});
  }, [authorizedFetch, applySnapshot]);

  const requestRefresh = useCallback(() => {
    if (refreshInFlightRef.current) {
      return refreshInFlightRef.current;
    }

    const task = refreshStates()
      .catch((error) => {
        console.error("Failed to refresh stream states", error);
      })
      .finally(() => {
        refreshInFlightRef.current = null;
      });

    refreshInFlightRef.current = task;
    return task;
  }, [refreshStates]);

  const setStreamState = useCallback(
    (streamId: string | null | undefined, state: StreamStateValue, message?: string | null) => {
      const key = streamId?.trim();
      if (!key) {
        return;
      }

      const nextMessage = parseMessage(message);

      setStreamStates((previous) => {
        const current = previous[key];
        const nextDetails: StreamStateDetails = nextMessage ? { state, message: nextMessage } : { state };

        if (current && current.state === nextDetails.state && current.message === nextDetails.message) {
          return previous;
        }

        return {
          ...previous,
          [key]: nextDetails
        };
      });
    },
    []
  );

  useEffect(() => {
    const handleUpdate = (payload: UpdatePayload) => {
      try {
        const rawId = payload?.streamId ?? "";
        const id = typeof rawId === "string" ? rawId.trim() : "";
        const state = parseState(payload?.state);
        if (!id || !state) {
          return;
        }

        const message = parseMessage(payload?.message);

        setStreamStates((previous) => {
          const next: StreamStatesMap = { ...previous };
          const current = previous[id];
          const nextDetails: StreamStateDetails = message ? { state, message } : { state };

          if (current && current.state === nextDetails.state && current.message === nextDetails.message) {
            return previous;
          }

          next[id] = nextDetails;
          return next;
        });
      } catch (error) {
        console.error("Failed to parse stream state update", error);
      }
    };

    const handleStreamStateMessage = (message: { type: string; data: unknown }) => {
      if (message.type === snapshotEventType) {
        applySnapshot(message.data as SnapshotPayload);
      } else if (message.type === changedEventType) {
        handleUpdate(message.data as UpdatePayload);
      }
    };

    // Subscribe to stream state messages from AppContext
    const unsubscribe = subscribeToStreamStateMessages(handleStreamStateMessage);

    return unsubscribe;
  }, [applySnapshot]);

  useEffect(() => {
    const wasConnected = wasConnectedRef.current;
    if (!wasConnected && eventStreamConnected) {
      void requestRefresh();
    }
    wasConnectedRef.current = eventStreamConnected;
  }, [eventStreamConnected, requestRefresh]);

  useEffect(() => {
    if (!eventStreamConnected) {
      syncedConnectionIdRef.current = null;
      return;
    }

    const normalizedConnectionId =
      typeof connectionId === "string" && connectionId.trim().length > 0
        ? connectionId.trim()
        : null;
    if (!normalizedConnectionId) {
      return;
    }

    if (syncedConnectionIdRef.current === normalizedConnectionId) {
      return;
    }
    syncedConnectionIdRef.current = normalizedConnectionId;
    void requestRefresh();
  }, [connectionId, eventStreamConnected, requestRefresh]);

  useEffect(() => {
    if (!eventStreamConnected || typeof window === "undefined") {
      return;
    }

    const interval = window.setInterval(() => {
      void requestRefresh();
    }, fallbackRefreshIntervalMs);
    return () => {
      window.clearInterval(interval);
    };
  }, [eventStreamConnected, requestRefresh]);

  const value = useMemo<StreamsStateContextValue>(
    () => ({
      streamStates,
      getStreamState: (streamId) => {
        const key = streamId?.trim();
        if (!key) {
          return "unknown";
        }
        return streamStates[key]?.state ?? "unknown";
      },
      getStreamStateMessage: (streamId) => {
        const key = streamId?.trim();
        if (!key) {
          return null;
        }
        return streamStates[key]?.message ?? null;
      },
      setStreamState
    }),
    [setStreamState, streamStates]
  );

  return <StreamsStateContext.Provider value={value}>{children}</StreamsStateContext.Provider>;
};

export const useStreamsState = () => {
  const context = useContext(StreamsStateContext);
  if (!context) {
    throw new Error("useStreamsState must be used within a StreamsStateProvider");
  }
  return context;
};
